/* ══════════════════════════════════════════════════════════════════════
   SEIZA NORTH · Custom Edition — launch corrections
   Loaded immediately AFTER seiza-custom.js.

   WHY A SEPARATE FILE
   seiza-custom.js holds the 400-entry Hipparcos catalogue and the astronomy.
   That data is verified and correct. Retyping it to reach three broken
   functions would risk a silent transcription error in a star coordinate —
   the one class of bug nobody would ever notice. So the catalogue is left
   untouched and every correction lives here.

   WHAT IT FIXES
   1. Add to cart was impossible. The section shipped an empty
      <input name="id" id="print-variant-id" value="">. Now driven by the real
      Shopify variants emitted as [data-seiza-variants].
   2. Displayed price was invented in JS (PRICE_MATRIX) and disagreed with
      what Shopify would actually charge. Price now comes from the variant.
   3. The export ran at ~254 dpi. Now 300 dpi, with the webfonts embedded —
      an SVG rasterised through an <img> gets no access to the page's fonts,
      so the hanko and the city name were falling back to a system serif in
      the file that went to print.
   4. The date on the poster could be a day early. new Date("1986-04-16") is
      parsed as UTC midnight, so every customer west of Greenwich saw April 15
      on a print bought for April 16.

   SIZE KEY — size only, no paper.
   The store sells one paper, so the product has a single Size option. The key
   is read from the active button's data-size, NOT from seizaCustom.state.size:
   the original script initialises that to 'B2', a value that no longer exists
   anywhere, and only corrects it on the first click. The DOM is authoritative.

   SHEET SIZE — 500 x 700 mm, not 707.
   The Gelato product is `..._ver_500x700-mm_20x28-inch_...` and 50 x 70 cm is
   the European frame standard. ISO B2 is 500 x 707 and would sit 7 mm proud of
   every one of those frames, so do not "correct" this to 707 on the strength
   of the product being called B2 elsewhere in the project. The name is wrong,
   the number is right.
   ══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const PRINT = { widthMM: 500, heightMM: 700, dpi: 300 };
  const PREVIEW = { w: 400, h: 540 };

  /* ───── Variant map, emitted by sections/seiza-custom-product.liquid ─────
     { "print": { "a4": {id, price, available}, "50x70": {...} },
       "digital": {id, price, available} }                                */
  let _variants = null;
  function variants() {
    if (_variants) return _variants;
    _variants = { print: {}, digital: null };
    const el = document.querySelector('[data-seiza-variants]');
    if (el) {
      try {
        _variants = Object.assign(_variants, JSON.parse(el.textContent) || {});
      } catch (e) {
        console.warn('SEIZA: variant map unreadable', e);
      }
    }
    return _variants;
  }

  function currentKey() {
    const btn = document.querySelector('#custom-sizes .size-btn.active');
    const s = (btn && btn.dataset.size) ||
              (window.seizaCustom && seizaCustom.state.size) || 'A4';
    return String(s).toLowerCase();
  }

  function syncPrintVariant() {
    const v = variants().print[currentKey()];
    const idInput = document.getElementById('print-variant-id');
    const btn = document.getElementById('btn-print');
    const priceEl = document.getElementById('print-price');
    const fromEl = document.querySelector('#print-form .option-price .from');
    if (fromEl) fromEl.style.display = 'none';

    if (!idInput || !btn) return;

    if (!v || !v.id) {
      idInput.value = '';
      btn.disabled = true;
      btn.textContent = 'Unavailable in this size';
      if (priceEl) priceEl.textContent = '—';
      return;
    }

    idInput.value = v.id;
    if (priceEl) priceEl.textContent = v.price;

    // Carry the chosen size to fulfilment as a line item property too.
    const sizeBtn = document.querySelector('#custom-sizes .size-btn.active');
    const propSize = document.getElementById('prop-size');
    if (sizeBtn && propSize) propSize.value = sizeBtn.dataset.name || sizeBtn.dataset.size;

    if (v.available === false) {
      btn.disabled = true;
      btn.textContent = 'Sold out';
      return;
    }
    const ready = window.seizaCustom && seizaCustom.state.previewReady;
    btn.disabled = !ready;
    btn.textContent = ready ? 'Add to cart — print →' : 'Generate first to order';
  }

  function syncDigitalVariant() {
    const d = variants().digital;
    const form = document.getElementById('digital-form');
    const idInput = document.getElementById('digital-variant-id');
    const btn = document.getElementById('btn-digital');
    if (!form) return;

    if (!d || !d.id) { form.style.display = 'none'; return; }
    form.style.display = '';
    if (idInput) idInput.value = d.id;
    const priceEl = form.querySelector('.option-price .price');
    if (priceEl) priceEl.textContent = d.price;
    if (btn) {
      const ready = window.seizaCustom && seizaCustom.state.previewReady;
      btn.disabled = !ready || d.available === false;
      btn.textContent = ready ? 'Add to cart — digital →' : 'Generate first to download';
    }
  }

  function syncAll() { syncPrintVariant(); syncDigitalVariant(); }

  /* ───── Date label: build from parts so it is local, never UTC ───── */
  function formatDateLabel(dateStr) {
    const parts = String(dateStr).split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function correctDateLabel() {
    const svg = document.querySelector('#custom-preview svg');
    if (!svg) return;
    const dateStr = (document.getElementById('map-date') || {}).value;
    const timeStr = (document.getElementById('map-time') || {}).value || '22:00';
    const label = formatDateLabel(dateStr);
    if (!label) return;
    const node = svg.querySelector('text.sky-label[font-style="italic"]');
    if (node) node.textContent = label + ' · ' + timeStr;
  }

  /* ───── Embed the webfonts into the exported SVG ─────
     An SVG loaded through <img> renders in an isolated document with no access
     to the page's stylesheets. Google's css2 endpoint takes a &text= parameter,
     so we fetch a subset containing only the glyphs this poster uses — a few KB
     rather than a megabyte of CJK.                                          */
  const FAMILY_SPEC = {
    'Shippori Mincho B1': 'Shippori+Mincho+B1:wght@400;500',
    'EB Garamond': 'EB+Garamond:ital@0;1',
    'DM Sans': 'DM+Sans:wght@400;500'
  };

  async function blobToDataURL(blob) {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  }

  async function buildFontCSS(svgEl) {
    const needed = {};
    svgEl.querySelectorAll('text, tspan').forEach((t) => {
      const raw = t.getAttribute('font-family');
      if (!raw) return;
      const fam = raw.split(',')[0].trim().replace(/['"]/g, '');
      if (!FAMILY_SPEC[fam]) return;
      needed[fam] = (needed[fam] || '') + (t.textContent || '');
    });

    let css = '';
    for (const fam of Object.keys(needed)) {
      const chars = Array.from(new Set(Array.from(needed[fam]))).join('');
      if (!chars.trim()) continue;
      try {
        const url = 'https://fonts.googleapis.com/css2?family=' + FAMILY_SPEC[fam] +
                    '&text=' + encodeURIComponent(chars);
        const sheet = await (await fetch(url)).text();
        const urls = [...sheet.matchAll(/url\((https:\/\/[^)]+)\)/g)].map(m => m[1]);
        let out = sheet;
        for (const u of urls) {
          const b = await (await fetch(u)).blob();
          out = out.replace(u, await blobToDataURL(b));
        }
        css += out + '\n';
      } catch (e) {
        console.warn('SEIZA: could not embed ' + fam + ' — the print file will fall back to a system serif.', e);
      }
    }
    return css;
  }

  async function downloadPrintPNG() {
    const svg = document.querySelector('#custom-preview svg');
    if (!svg) { alert('Generate a preview first.'); return; }

    const btn = document.getElementById('admin-download-btn');
    const restore = btn ? btn.textContent : null;
    if (btn) { btn.disabled = true; btn.textContent = 'Rendering…'; }

    try {
      const cloned = svg.cloneNode(true);
      cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const wm = cloned.querySelector('.sky-watermark');
      if (wm) wm.remove();
      const fr = cloned.querySelector('.sky-frame');
      if (fr) fr.remove();

      const fontCSS = await buildFontCSS(cloned);

      // Preview canvas is 400x540. Scale to the full 500 mm width (x1.25 -> 675
      // tall) and centre it on the 700 mm sheet, leaving an even kinari margin.
      const scale = PRINT.widthMM / PREVIEW.w;          // 1.25
      const contentH = PREVIEW.h * scale;               // 675
      const offsetY = (PRINT.heightMM - contentH) / 2;  // 12.5

      const sheetSvg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="' + PRINT.widthMM + 'mm" height="' + PRINT.heightMM + 'mm" ' +
        'viewBox="0 0 ' + PRINT.widthMM + ' ' + PRINT.heightMM + '">' +
        (fontCSS ? '<style>' + fontCSS + '</style>' : '') +
        '<rect width="' + PRINT.widthMM + '" height="' + PRINT.heightMM + '" fill="#f3e9d2"/>' +
        '<g transform="translate(0 ' + offsetY + ') scale(' + scale + ')">' + cloned.innerHTML + '</g>' +
        '</svg>';

      const px = (mm, dpi) => Math.round(mm / 25.4 * dpi);
      const dateStr = (document.getElementById('map-date') || {}).value || 'sky';
      const cityStr = (window.seizaCustom && seizaCustom.state.resolvedCity) ||
                      (document.getElementById('map-city') || {}).value || 'custom';
      const safeCity = cityStr.trim().split(',')[0].toLowerCase().replace(/[^a-z0-9]/g, '-');

      for (const dpi of [PRINT.dpi, 200, 150]) {
        const blob = await rasterise(sheetSvg, px(PRINT.widthMM, dpi), px(PRINT.heightMM, dpi));
        if (blob) {
          if (dpi < PRINT.dpi) {
            alert('This browser could not render at ' + PRINT.dpi + ' dpi, so the file is ' + dpi +
                  ' dpi. Chrome handles the full size — re-export there before sending to Gelato.');
          }
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'SEIZA-NORTH-' + safeCity + '-' + dateStr + '-50x70-' + dpi + 'dpi.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(a.href);
          return;
        }
      }
      alert('Could not export the print file in this browser. Try Chrome.');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = restore; }
    }
  }

  function rasterise(svgMarkup, w, h) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' }));
      const img = new Image();
      img.onload = function () {
        let canvas;
        try {
          canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#f3e9d2';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          // A canvas over the browser's area limit silently yields transparent black.
          const probe = ctx.getImageData(Math.floor(w / 2), 10, 1, 1).data;
          if (probe[3] === 0 || (probe[0] === 0 && probe[1] === 0 && probe[2] === 0)) {
            URL.revokeObjectURL(url); resolve(null); return;
          }
        } catch (e) {
          URL.revokeObjectURL(url); resolve(null); return;
        }
        URL.revokeObjectURL(url);
        canvas.toBlob((b) => resolve(b || null), 'image/png');
      };
      img.onerror = function () { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  /* ───── Install ───── */
  function install() {
    if (!window.seizaCustom) return;

    const original = window.seizaCustom.generateMap.bind(window.seizaCustom);
    window.seizaCustom.generateMap = async function (e) {
      await original(e);
      correctDateLabel();
      syncAll();
    };

    document.querySelectorAll('#custom-sizes .size-btn')
      .forEach((b) => b.addEventListener('click', () => setTimeout(syncAll, 0)));

    const adminBtn = document.getElementById('admin-download-btn');
    if (adminBtn) {
      const fresh = adminBtn.cloneNode(true);   // drops the old listener
      adminBtn.parentNode.replaceChild(fresh, adminBtn);
      fresh.addEventListener('click', downloadPrintPNG);
      fresh.textContent = 'Download print PNG (50×70 cm · 300 dpi) ↓';
    }

    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(install, 0));
  } else {
    setTimeout(install, 0);
  }

  window.seizaCustomFixes = { downloadPrintPNG, syncAll, formatDateLabel, currentKey };
})();
